// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The cabinet visibly moves when you shove it (M5-11).
//
// "The cabinet **visibly moves** — a few millimetres, springing back — and the whole machine,
// backbox included, moves with it. This visual is the single most convincing moment in the
// product." (06 §2.3.)
//
// A damped harmonic oscillator per axis, plus a yaw that comes free from the fact that a real
// cabinet on four legs pivots slightly about its own centre when shoved off-axis. Nothing here
// touches the simulation: this is the *machine's* displacement in the room, and the ball inside
// it is nudged by `World.nudge` on its own path (PH-070). Two independent representations of one
// shove, deliberately — the physical one has to be exactly right and the visual one has to look
// right, and coupling them would mean the ball's behaviour changed when somebody tuned the
// wobble.
//
// **A few millimetres, and not one more.** 4 mm at the hardest legal shove. A cabinet that moves
// a visible distance in a headset reads as a cardboard box: the thing being simulated weighs
// about 120 kg and is standing on four bolted legs, so what a shove does to it is *almost*
// nothing, and "almost nothing, immediately, with a settle" is what sells the weight. Every
// version of this that looked wrong looked wrong by moving too far.
//
// **Frame-rate independent by construction.** The oscillator is integrated semi-implicitly with
// the real frame time and sub-stepped when a frame runs long, so a dropped frame produces a
// slightly coarser wobble rather than an exploding one. At 72 Hz in a headset and 120 Hz on a
// desktop the settle takes the same number of milliseconds, which is the only way it can be
// tuned once.

/** Where the cabinet is, relative to where it was bolted down. Metres and radians. */
export interface CabinetOffset {
  x: number
  z: number
  yaw: number
}

/**
 * Natural frequency, radians per second.
 *
 * 34 rad/s ≈ 5.4 Hz. A real cabinet rocking on its legs is a stiff, fast, short thing — you hear
 * it more than you see it. Below about 4 Hz it starts to read as a wobble-board; above 8 Hz the
 * displacement is over before a 72 Hz display has drawn three frames of it.
 */
export const SPRING_OMEGA = 34

/**
 * Damping ratio.
 *
 * 0.42 — under-damped, so there is exactly one visible overshoot and then it is gone. Critical
 * damping (1.0) looks like the cabinet is standing in treacle; anything under about 0.3 rings
 * for long enough to still be moving when the ball comes back down, which is distracting at
 * precisely the wrong moment.
 */
export const SPRING_ZETA = 0.42

/** Metres of displacement per newton-second of impulse. 34 N·s (a full shove) gives 4 mm. */
export const METRES_PER_IMPULSE = 0.004 / 34

/** Radians of yaw per newton-second of *off-centre* impulse. A shove from the side twists it. */
export const YAW_PER_IMPULSE = 0.0025 / 34

/**
 * Longest step the integrator takes before sub-stepping, seconds.
 *
 * 1/480, which is far shorter than any frame. It was 1/120 and that was too coarse to keep the
 * promise this class makes: semi-implicit Euler carries a phase error of order `ω·dt`, so a
 * 72 Hz frame (sub-stepped to 1/144) and a 240 Hz frame ran the same wobble at measurably
 * different phases, and the tails differed by a factor of thirteen. Seven multiplies a frame
 * buys a settle that is the same settle on every device, which is the entire reason the
 * sub-stepper exists.
 */
const MAX_SUB_STEP = 1 / 480

export class CabinetSpring {
  private x = 0
  private z = 0
  private yaw = 0
  private vx = 0
  private vz = 0
  private vYaw = 0

  /** The current offset. Read every frame by the renderer; never allocated. */
  readonly offset: CabinetOffset = { x: 0, z: 0, yaw: 0 }

  /**
   * A shove landed. `ix`/`iy` are the playfield-plane impulse the simulation was given, so the
   * visual and the physical come from one number.
   *
   * Applied as a velocity change rather than a displacement, which is what an impulse is: the
   * cabinet starts moving and the spring stops it. Setting the position directly would make it
   * teleport a few millimetres and then oscillate about the new place, which reads as a glitch.
   */
  shove(ix: number, iy: number): void {
    // Playfield +Y is cabinet −Z; see the note in `bodyNudge.ts`.
    this.vx += ix * METRES_PER_IMPULSE * SPRING_OMEGA
    this.vz += -iy * METRES_PER_IMPULSE * SPRING_OMEGA
    // A shove along X twists the body; a straight push from the front does not.
    this.vYaw += ix * YAW_PER_IMPULSE * SPRING_OMEGA
  }

  /** Advance the wobble by a real frame. */
  advance(frameSeconds: number): CabinetOffset {
    let remaining = frameSeconds > 0 ? frameSeconds : 0
    // A frame longer than a quarter second is a tab that was in the background. Settling
    // instantly is right there: catching up would spend the whole budget integrating a wobble
    // nobody saw.
    if (remaining > 0.25) {
      this.settle()
      return this.publish()
    }
    while (remaining > 0) {
      const dt = remaining > MAX_SUB_STEP ? MAX_SUB_STEP : remaining
      remaining -= dt
      this.integrate(dt)
    }
    return this.publish()
  }

  /** Stop dead and stand up straight. Used when the machine is re-placed or the session ends. */
  settle(): void {
    this.x = 0
    this.z = 0
    this.yaw = 0
    this.vx = 0
    this.vz = 0
    this.vYaw = 0
  }

  private integrate(dt: number): void {
    const k = SPRING_OMEGA * SPRING_OMEGA
    const c = 2 * SPRING_ZETA * SPRING_OMEGA
    // Semi-implicit Euler: velocity first, then position with the new velocity. Unconditionally
    // stable for this system at every step size the sub-stepper produces, where explicit Euler
    // gains energy on every step and a spring that gains energy is a cabinet that shakes itself
    // apart after a minute of play.
    this.vx += (-k * this.x - c * this.vx) * dt
    this.vz += (-k * this.z - c * this.vz) * dt
    this.vYaw += (-k * this.yaw - c * this.vYaw) * dt
    this.x += this.vx * dt
    this.z += this.vz * dt
    this.yaw += this.vYaw * dt
  }

  private publish(): CabinetOffset {
    this.offset.x = this.x
    this.offset.z = this.z
    this.offset.yaw = this.yaw
    return this.offset
  }
}
